import { Loading } from '@getslot/ui-web/notice'
import type { QueryClient } from '@tanstack/react-query'
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  useRouterState,
} from '@tanstack/react-router'
import { Suspense } from 'react'
import { AuthGate } from './shared/auth/auth-gate'
import { AppShell } from './shared/ui/app-shell'
import { RouteError } from './shared/ui/route-error'

interface RouterContext {
  queryClient: QueryClient
}

/** Документы открыты без входа: на них ссылается приветствие бота. */
const PUBLIC_PREFIX = '/legal'

function Root() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (pathname === PUBLIC_PREFIX || pathname.startsWith(`${PUBLIC_PREFIX}/`)) {
    return (
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    )
  }
  return (
    <AuthGate>
      <Suspense fallback={<Loading />}>
        <AppShell>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </AppShell>
      </Suspense>
    </AuthGate>
  )
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  errorComponent: RouteError,
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./features/home/home-page'), 'HomePage'),
  errorComponent: RouteError,
})

const newRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/new',
  component: lazyRouteComponent(() => import('./features/wizard/embassy-page'), 'EmbassyPage'),
  errorComponent: RouteError,
})

const applicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$id',
  component: lazyRouteComponent(() => import('./features/application/application-page'), 'ApplicationPage'),
  errorComponent: RouteError,
})

const datesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$id/dates',
  component: lazyRouteComponent(() => import('./features/wizard/dates-page'), 'DatesPage'),
  errorComponent: RouteError,
})

const cabinetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$id/cabinet',
  component: lazyRouteComponent(() => import('./features/wizard/cabinet-page'), 'CabinetPage'),
  errorComponent: RouteError,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('./features/profile/profile-page'), 'ProfilePage'),
  errorComponent: RouteError,
})

const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal',
  component: lazyRouteComponent(() => import('./features/legal/legal-pages'), 'LegalListPage'),
  errorComponent: RouteError,
})

const legalDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$doc',
  component: lazyRouteComponent(() => import('./features/legal/legal-pages'), 'LegalDocumentPage'),
  errorComponent: RouteError,
})

const routeTree = rootRoute.addChildren([
  homeRoute,
  newRoute,
  applicationRoute,
  datesRoute,
  cabinetRoute,
  profileRoute,
  legalRoute,
  legalDocumentRoute,
])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent' })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
